import { useCallback, useEffect, useState } from "react";
import { Controller, useForm, useFormContext, useWatch } from "react-hook-form";
import {
  Box,
  Button,
  Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { ContentCopy as CopyIcon } from "@mui/icons-material";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import PropTypes from "prop-types";

import Form from "../Form/Form.jsx";
import FormTextField from "../Form/FormTextField.jsx";
import FormEmailInput from "../Form/FormEmailInput.jsx";
import FormAutocomplete from "../Form/FormAutocomplete.jsx";
import FormDatePicker from "../Form/FormDatePicker.jsx";
import SubmitButton from "../Form/SubmitButton.jsx";
import DsButton from "../../theme/components/Button.jsx";
import { backendGet } from "../../services/dataService.js";
import { fedfKommunikation } from "formdefs/kommunikation.js";
import { colorsFoundation, typographyTokens } from "../../theme/index.js";
import Alert from "@mui/material/Alert";
import { enqueueSnackbar, useSnackbar } from "notistack";

// ---------------------------------------------------------------------------
// Template button definitions (extracted from legacy formdef)
// ---------------------------------------------------------------------------
const FILL_TEMPLATES = fedfKommunikation.formdefinition
  .filter((item) => item.control === "fillbutton")
  .map((item) => ({
    label: item.label,
    actions: item.actions,
    // null = always visible, true = visible when an_versicherer, false = visible when !an_versicherer
    visibleWhenAnVersicherer:
      item.hidden === "isEqualOther('an_versicherer', true)"
        ? false
        : item.hidden === "isEqualOther('an_versicherer', false)"
          ? true
          : null,
  }));

// for "due on" buttons
const QUICK_DATES = [
  { label: "in 1 Woche", days: 7 },
  { label: "in 2 Wochen", days: 14 },
  { label: "in 1 Monat", days: 30 },
  // { label: "nicht erinnern", days: null },
];

const getDateInFuture = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  date.setHours(0, 0, 0, 0);

  return date;
};

// ---------------------------------------------------------------------------
// Validation schema
// ---------------------------------------------------------------------------
const validationSchema = yup.object({
  betreff: yup.string().required("Betreff ist erforderlich"),
  beschreibung: yup.string().required("Beschreibung ist erforderlich"),
  an_weitere: yup.array().of(yup.string().email()),
});

// ---------------------------------------------------------------------------
// Sub-component: Template fill buttons
// ---------------------------------------------------------------------------
const TemplateButtons = () => {
  const { setValue } = useFormContext();
  const an_versicherer = useWatch({ name: "an_versicherer" });
  const visible = FILL_TEMPLATES.filter(
    (t) =>
      t.visibleWhenAnVersicherer === null ||
      t.visibleWhenAnVersicherer === an_versicherer
  );

  if (!visible.length) return null;

  return (
    <Grid container spacing={1}>
      {visible.map((template, index) => (
        <Grid key={index} size={{ md: 4, lg: 2 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              template.actions.forEach((action) => {
                setValue(action.target, action.value, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                });
              });
            }}
            sx={{
              fontSize: typographyTokens.size.sm,
              fontWeight: typographyTokens.weight.regular,
              textTransform: "none",
            }}
          >
            {template.label}
          </Button>
        </Grid>
      ))}
    </Grid>
  );
};

const RecipientList = ({ recipients }) => {
  const an_versicherer = useWatch({ name: "an_versicherer" });

  const { enqueueSnackback } = useSnackbar();
  return (
    <>
      {an_versicherer && recipients?.insurer?.email && (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box>An:</Box>
          <Box>
            <strong>{recipients?.insurer.name}</strong>{" "}
            {`<${recipients.insurer.email.trim()}>`}
            <Typography
              variant="body2"
              sx={{ color: colorsFoundation.neutral[600] }}
            >
              Versicherer
            </Typography>
          </Box>
        </Box>
      )}

      {!an_versicherer && recipients?.claim_creator?.email && (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box>An:</Box>
          <Box>
            <strong>{recipients?.claim_creator.name}</strong>{" "}
            {`<${recipients.claim_creator.email.trim()}>`}{" "}
            <Typography
              variant="body2"
              sx={{ color: colorsFoundation.neutral[600] }}
            >
              Schadenersteller/Kunde - erhält eine Benachrichtigung über
              gesendete Mitteilung
            </Typography>
          </Box>
        </Box>
      )}

      {!an_versicherer && recipients?.claim_contact?.email && (
        <Alert severity="info">
          <Typography>Abweichende Kontaktperson</Typography>
          <Typography variant="body2">
            Im Schadenformular ist folgender Kontakt hinterlegt. Um eine E-Mail
            an diesen Kontakt zu senden, tragen Sie bitte die E-Mailadresse in
            das Feld "Weitere Empfänger" ein.
          </Typography>
          <br />
          <strong>{recipients?.claim_contact.name}</strong>{" "}
          {`<${recipients.claim_contact.email.trim()}>`}{" "}
          <IconButton
            size="small"
            sx={{ p: 0.5 }}
            onClick={() => {
              navigator.clipboard.writeText(recipients.claim_contact.email);
              enqueueSnackbar("E-Mail kopiert", { variant: "success" });
            }}
          >
            <CopyIcon
              sx={{
                fontSize: "1rem",
                mr: 1,
                color: colorsFoundation.key.blue[700],
              }}
            />
          </IconButton>
          <Typography variant="body2">
            Ansprechpartner aus Schadenformular/Kunde
          </Typography>
        </Alert>
      )}

      {recipients?.assigned_clerk?.name && (
        <Box sx={{ mb: 1 }}>
          An: <strong>{recipients?.assigned_clerk.name}</strong>{" "}
          {`<${recipients.assigned_clerk.email}>`}
          <Typography>Ihr Sachbearbeiter</Typography>
        </Box>
      )}
    </>
  );
};

const Recipient = ({ disabled }) => {
  const { control } = useFormContext();
  return (
    <Controller
      name="an_versicherer"
      control={control}
      render={({ field }) => {
        return (
          <ToggleButtonGroup
            exclusive
            disabled={disabled}
            sx={{
              borderRadius: 8,
              overflow: "hidden",

              "& .MuiToggleButtonGroup-grouped": {
                borderRadius: 0,
                border: 0,
              },

              "& .MuiToggleButtonGroup-grouped:first-of-type": {
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
              },
              "& .MuiToggleButtonGroup-grouped:last-of-type": {
                borderTopRightRadius: 8,
                borderBottomRightRadius: 8,
              },

              "& .MuiToggleButton-root": {
                backgroundColor: colorsFoundation.neutral[50],
                color: colorsFoundation.neutral[800],
                borderWidth: 1,
                borderColor: colorsFoundation.neutral[300],
              },

              "& .MuiToggleButton-root.Mui-selected": {
                backgroundColor: colorsFoundation.key.blue[700],
                color: "primary.contrastText",
                borderColor: colorsFoundation.key.blue[700],
              },
              "& .MuiToggleButton-root.Mui-selected:hover": {
                backgroundColor: colorsFoundation.key.blue[800],
              },
            }}
            value={field.value}
            onChange={(e, value) => {
              if (value !== null) field.onChange(value);
            }}
          >
            <ToggleButton value={false} color="primary">
              Kunde
            </ToggleButton>
            <ToggleButton value={true} color="primary">
              Versicherer
            </ToggleButton>
          </ToggleButtonGroup>
        );
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// Sub-component: Quick date buttons for "faellig_am"
// ---------------------------------------------------------------------------
const QuickDateButtons = ({ disabled }) => {
  const { setValue } = useFormContext();
  const faellig_am = useWatch({ name: "faellig_am" });

  if (disabled) return null;

  const handleClick = (days) => {
    if (days === null) {
      setValue("faellig_am", null);
      return;
    }

    setValue("faellig_am", getDateInFuture(days), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        flexWrap: "wrap",
        height: "100%",
        alignItems: "center",
      }}
    >
      {QUICK_DATES.map((option) => {
        const isSelected =
          faellig_am &&
          getDateInFuture(option.days).toDateString() ===
            new Date(faellig_am).toDateString();

        return (
          <DsButton
            key={option.days}
            size="small"
            variant={isSelected ? "secondary" : "tertiary"}
            onClick={() => handleClick(option.days)}
          >
            {option.label}
          </DsButton>
        );
      })}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Sub-component: Save Draft button (bypasses validation)
// ---------------------------------------------------------------------------
const SaveDraftButton = ({ onSaveDraft }) => {
  const { getValues } = useFormContext();
  const [submitting, setSubmitting] = useState(false);

  const handleClick = async () => {
    setSubmitting(true);
    try {
      await onSaveDraft(getValues());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DsButton
      type="button"
      variant="secondary"
      onClick={handleClick}
      disabled={submitting}
    >
      {submitting ? "Wird gespeichert..." : "Entwurf speichern"}
    </DsButton>
  );
};

SaveDraftButton.propTypes = {
  onSaveDraft: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// Main form component
// ---------------------------------------------------------------------------
const CommunicationEmailForm = ({
  defaultValues,
  onSubmit,
  onSaveDraft,
  onCancel,
  canMessageInsurer,
  schadenId,
  kommunikationId,
  disabled,
  submitLabel,
  submitRequireDirty = true,
}) => {
  const fetchAnlagen = useCallback(async () => {
    let options = [];
    await backendGet(`data/anlages_select/${schadenId}`, {
      setResult: (result) => {
        options = result || [];
      },
    });
    return options;
  }, [schadenId]);

  const [previewRecipients, setPreviewRecipients] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    const fetchPreview = async () => {
      await backendGet(`data/kommunikations/${schadenId}/preview-recipients`, {
        setResult: (res) => {
          setPreviewRecipients(res?.data?.recipients || []);
        },
        setIsLoading: setPreviewLoading,
      });
    };

    fetchPreview();
  }, [schadenId]);

  const handleSubmit = async (data) => {
    await onSubmit(data);
  };

  return (
    <Form
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      resolver={yupResolver(validationSchema)}
      watchFields={["an_versicherer"]}
      context={{ canMessageInsurer }}
      mode="onChange"
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        {/* fields for users that have the ability to write to the insurance company */}
        {canMessageInsurer ? (
          <>
            <Recipient disabled={!!kommunikationId} />

            <RecipientList recipients={previewRecipients} />

            <FormEmailInput
              name="an_weitere"
              label="Weitere E-Mail-Empfänger"
              helperText="Geben Sie ggfs. weitere E-Mailadressen ein, die diese Mitteilung erhalten sollen"
              disabled={disabled}
            />
            {/* Template buttons (only in edit mode) */}
            {!disabled && <TemplateButtons />}
          </>
        ) : (
          <RecipientList recipients={previewRecipients} />
        )}

        {/* Subject */}
        <FormTextField
          name="betreff"
          label="Betreff"
          fullWidth
          disabled={disabled}
        />

        {/* Body */}
        <FormTextField
          name="beschreibung"
          label="Beschreibung"
          fullWidth
          multiline
          rows={12}
          disabled={disabled}
        />

        {/* Attachments */}
        <FormAutocomplete
          name="anlagen"
          label="Anlagen"
          multiple
          fetchOptions={fetchAnlagen}
          disabled={disabled}
          transformValue={(option) => option ?? null}
        />

        {/* Reminder Due Date */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormDatePicker
              name="faellig_am"
              label="Erinnern am"
              disabled={disabled}
              clearable={true}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <QuickDateButtons disabled={disabled} />
          </Grid>
        </Grid>

        {/* Info text */}
        {canMessageInsurer && (
          <Typography variant="body2" color="text.secondary">
            Hinweis: die Platzhalter werden beim Speichern automatisch ersetzt.
            Beim Versand per E-Mail wird außerdem automatisch eine E-Mail
            Signatur hinzugefügt.
          </Typography>
        )}

        {/* Action buttons */}
        {!disabled && (
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", pt: 1 }}
          >
            <DsButton variant="tertiary" onClick={onCancel}>
              Abbrechen
            </DsButton>
            {onSaveDraft && <SaveDraftButton onSaveDraft={onSaveDraft} />}
            <SubmitButton requireDirty={submitRequireDirty}>
              {submitLabel || "Speichern"}
            </SubmitButton>
          </Box>
        )}

        {disabled && (
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", pt: 1 }}
          >
            <Button variant="text" onClick={onCancel}>
              Schließen
            </Button>
          </Box>
        )}
      </Box>
    </Form>
  );
};

CommunicationEmailForm.propTypes = {
  defaultValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSaveDraft: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  canMessageInsurer: PropTypes.bool,
  schadenId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  kommunikationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  submitLabel: PropTypes.string,
  submitRequireDirty: PropTypes.bool,
};

export default CommunicationEmailForm;
